"use client";

// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → now central widget_impression
// (TrackedWidget in NewPage); see webapp/docs/analytics
// import { FireWidgetViewedEvent } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import Image from "next/image";
import { useEffect, useMemo, useRef, useState } from "react";

interface MetaItem {
  id?: string | number;
  meta_image?: string;
  meta_alt_image?: string;
  /** Card title — also shown as the main heading on the right when selected. */
  meta_heading?: string;
  meta_description?: string;
  meta_priority: number;
  meta_status?: boolean;
}

interface Accreditation4Data {
  id: number;
  page_id?: number;
  status?: boolean | number;
  background_color?: string;
  title_primary_color?: string;
  body_text_color?: string;
  card_active_bg_color?: string;
  card_inactive_bg_color?: string;
  card_title_color?: string;
  metadata?: MetaItem[];
}

interface Accreditation4Props {
  data: Accreditation4Data | { data: Accreditation4Data };
}

function resolvePayload(
  data: Accreditation4Props["data"]
): Accreditation4Data | undefined {
  if (!data || typeof data !== "object") return undefined;
  const anyData = data as Record<string, unknown>;
  if ("data" in anyData && anyData.data != null && typeof anyData.data === "object") {
    return anyData.data as Accreditation4Data;
  }
  return data as Accreditation4Data;
}

const Accreditation4 = ({ data }: Accreditation4Props) => {
  const ref = useRef<HTMLElement>(null);
  const section = resolvePayload(data);

  const {
    background_color = "#FFFFFF",
    title_primary_color = "#171719",
    body_text_color = "#373737",
    card_active_bg_color = "#FFEFEE",
    card_inactive_bg_color = "#F4F4F4",
    card_title_color = "#171719",
    metadata = [],
  } = section || {};

  const sortedItems = useMemo(() => {
    if (!metadata?.length) return [];
    return [...metadata]
      .filter((item) => item?.meta_status !== false)
      .sort((a, b) => a.meta_priority - b.meta_priority);
  }, [metadata]);

  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    setActiveIndex(0);
  }, [sortedItems.length]);

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "accreditation_section");
  //   }
  // }, []);

  const st = section?.status;
  const active = st === true || st === 1 || String(st) === "1";
  if (!active) {
    return null;
  }

  if (sortedItems.length === 0) {
    return null;
  }

  const activeItem = sortedItems[Math.min(activeIndex, sortedItems.length - 1)];

  return (
    <section
      ref={ref}
      className={cn(
        "[&_*]:![font-family:Poppins,sans-serif]",
        "relative flex w-full justify-center",
        "px-6 py-8 min-[1024px]:px-16 min-[1024px]:py-12 min-[1280px]:px-[104px] min-[1280px]:py-[72px]"
      )}
      style={{
        backgroundColor: background_color,
        fontFamily: "Poppins, sans-serif",
      }}
    >
      <div
        className={cn(
          "flex w-full max-w-[1200px] flex-col items-stretch gap-8",
          "min-[1024px]:flex-row min-[1024px]:items-start min-[1024px]:justify-center min-[1024px]:gap-[24px]"
        )}
      >
        {/* Left: selectable cards */}
        <div className="flex w-full shrink-0 flex-col gap-3 min-[1024px]:w-[318px]">
          {sortedItems.map((item, index) => {
            const isActive = index === activeIndex;
            return (
              <button
                key={`${item.id ?? index}-${index}`}
                type="button"
                onClick={() => setActiveIndex(index)}
                aria-pressed={isActive}
                className="flex h-[92px] w-full items-center gap-[14px] self-stretch rounded-lg p-3 text-left font-medium transition-colors duration-200 min-[1024px]:w-[318px]"
                style={{
                  backgroundColor: isActive
                    ? card_active_bg_color
                    : card_inactive_bg_color,
                }}
              >
                {item.meta_image ? (
                  <span className="relative flex h-[72px] w-[72px] shrink-0 items-center justify-center overflow-hidden rounded-md bg-white">
                    <Image
                      src={item.meta_image}
                      alt={item.meta_alt_image || item.meta_heading || ""}
                      width={72}
                      height={72}
                      className="h-full w-full object-contain"
                    />
                  </span>
                ) : null}
                <span
                  className="text-base leading-snug"
                  style={{ color: card_title_color }}
                >
                  {item.meta_heading}
                </span>
              </button>
            );
          })}
        </div>

        {/* Right: selected item title + description */}
        {/* Stacked (mobile/tablet): heading + copy centered. Two-column from min-[1024px]: left-aligned beside the cards. */}
        <div className="flex w-full flex-col text-center min-[1024px]:min-h-[110px] min-[1024px]:w-[692px] min-[1024px]:max-w-[692px] min-[1024px]:pt-1 min-[1024px]:text-left">
          {activeItem?.meta_heading ? (
            <h2
              className="mb-3 text-[28px] font-semibold leading-tight min-[1024px]:text-[30px] min-[1280px]:text-[32px]"
              style={{ color: title_primary_color }}
            >
              {activeItem.meta_heading}
            </h2>
          ) : null}
          {activeItem?.meta_description ? (
            <p
              className="text-base font-normal leading-normal min-[1024px]:text-[17px] min-[1280px]:text-[18px]"
              style={{ color: body_text_color }}
            >
              {activeItem.meta_description}
            </p>
          ) : null}
        </div>
      </div>
    </section>
  );
};

export default Accreditation4;
